import * as Yup from 'yup';
import {TextAreaInput} from 'components/Forms/FormixComponents';
import {FormixFormWithData} from 'components/Forms/FormixFormWithData';
import {PythonHighlighter} from 'components/syntaxHighlighter/PythonHighlighter';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import type {ApplicantData} from 'course/application/firebase/models/applicantData';
import type {DebugInfo} from 'course/application/firebase/models/debugData';
import {useMemo} from 'react';

export const DebuggingForm = ({
  serverData,
  onSubmit,
  backUrl,
  debugData,
}: {
  serverData: ApplicantData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  onSubmit: (data: any) => void;
  backUrl: string;
  debugData: DebugInfo;
}) => {
  const t = useTranslateFunction('application');
  // Generate dynamic form format based on debugData
  const dynamicFormFormat = useMemo(
    () => generateDebuggingFormFormat(debugData, t),
    [debugData, t],
  );

  const serverDataForDebugging = serverData.debugAnswers || {};

  return (
    <div className="d-flex justify-content-center mt-3 mb-3">
      <div style={{maxWidth: 700, width: 'calc(100% - 20px)'}}>
        <FormixFormWithData
          serverData={serverDataForDebugging}
          backUrl={backUrl}
          onSubmit={onSubmit}
          formFormat={dynamicFormFormat}
        />
      </div>
    </div>
  );
};

const createDebuggingSection = (debugItem, t) => {
  const placeholder = debugItem.inputPlaceholder || t('Enter your response...');
  return ({name}) => (
    <>
      <h4>{debugItem.title}</h4>
      <p>{debugItem.description}</p>
      <PythonHighlighter code={debugItem.code} fontSize={14} />
      <TextAreaInput label="" name={name} placeholder={placeholder} />
    </>
  );
};

const generateDebuggingFormFormat = (
  debugData: DebugInfo,
  t: (key: string) => string,
) => {
  if (!debugData || !Array.isArray(debugData)) {
    return [];
  }

  return debugData.map(debugItem => ({
    // We use the title as the name, but could also use ID if we need to be unique
    name: debugItem.title,
    input: createDebuggingSection(debugItem, t),
    validation: Yup.string().required(
      debugItem.inputErrorMessage ||
        `${debugItem.title || t('This field')} ${t('is required')}`,
    ),
  }));
};
